import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Plus,
  ClipboardList,
  Check,
  X,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Pencil,
  Star,
  Clock,
  Search,
  Info,
  CheckCircle2,
  FileText,
  Paperclip,
  CalendarDays,
} from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import {
  useGetTasksQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useGetPendingSubmissionsQuery,
  useGradeSubmissionMutation,
} from "../../app/api/tasksApi";
import type {
  TaskWithSubmission,
  PendingSubmission,
} from "../../app/api/tasksApi";
import type { UserRole } from "../../app/api/authApi";
import type { QuarterKey } from "../../app/api/programmeApi";

/* ─────────────────────────────────────────────────────────────────
   ROLE CONSTANTS
───────────────────────────────────────────────────────────────── */

const CONTENT_ROLES: UserRole[] = [
  "admin",
  "committee_chair",
  "committee_member",
  "portal_manager",
  "regional_rep",
  "co",
  "cyp",
  "ryp",
];
const GRADER_ROLES: UserRole[] = [
  "admin",
  "committee_chair",
  "regional_rep",
  "co",
  "cyp",
  "ryp",
  "mentor",
];

const QUARTERS: QuarterKey[] = ["Q1", "Q2", "Q3", "Q4"];

/* ─────────────────────────────────────────────────────────────────
   FORM STATE
───────────────────────────────────────────────────────────────── */

interface TaskFormProps {
  quarter: QuarterKey;
  taskNumber: string;
  title: string;
  description: string;
  minWords: string;
  tags: string;
  availableFrom: string;
  dueDate: string;
  isPublished: boolean;
  instructions: { _id: string; stepNumber: string; instruction: string }[];
}

const EMPTY_FORM: TaskFormProps = {
  quarter: "Q1",
  taskNumber: "",
  title: "",
  description: "",
  minWords: "300",
  tags: "",
  availableFrom: "",
  dueDate: "",
  isPublished: false,
  instructions: [],
};

const taskToForm = (t: TaskWithSubmission): TaskFormProps => ({
  quarter: t.quarter,
  taskNumber: String(t.taskNumber),
  title: t.title,
  description: t.description ?? "",
  minWords: String(t.minWords),
  tags: t.tags?.join(", ") ?? "",
  availableFrom: t.availableFrom ? t.availableFrom.slice(0, 10) : "",
  dueDate: t.dueDate ? t.dueDate.slice(0, 10) : "",
  isPublished: t.isPublished,
  instructions: (t.instructions ?? []).map((ins) => ({
    _id: Math.random().toString(36),
    stepNumber: String(ins.stepNumber),
    instruction: ins.instruction,
  })),
});

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const FL: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <label className="block text-xs font-semibold text-gray-700 mb-1">
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (p) => (
  <input
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition disabled:bg-gray-50 disabled:text-gray-400 ${p.className ?? ""}`}
  />
);

const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (
  p,
) => (
  <textarea
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
      placeholder-gray-400 transition resize-none ${p.className ?? ""}`}
  />
);

const Sel: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (p) => (
  <select
    {...p}
    className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-white
      focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition ${p.className ?? ""}`}
  />
);

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  sub?: string;
}> = ({ checked, onChange, label, sub }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <div className="relative mt-0.5 shrink-0">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div
        className={`w-9 h-5 rounded-full transition-colors ${checked ? "bg-purple-600" : "bg-gray-200"}`}
      />
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`}
      />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </label>
);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

const ScoreRing: React.FC<{ score: number; size?: number }> = ({
  score,
  size = 44,
}) => {
  const pct = (score / 10) * 100;
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const colour = score >= 8 ? "#22c55e" : score >= 5 ? "#f59e0b" : "#ef4444";

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#f3f4f6"
        strokeWidth={5}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={colour}
        strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dy="0.35em"
        fontSize={size * 0.28}
        fontWeight="700"
        fill={colour}
      >
        {score}
      </text>
    </svg>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TASK DEFINITION CARD
───────────────────────────────────────────────────────────────── */

const TaskCard: React.FC<{
  task: TaskWithSubmission;
  canEdit: boolean;
  onEdit: (t: TaskWithSubmission) => void;
  onTogglePublish: (t: TaskWithSubmission) => void;
}> = ({ task, canEdit, onEdit, onTogglePublish }) => {
  const days = task.daysRemaining;
  const overdue = days !== null && days < 0;

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-all duration-150
      ${task.isPublished ? "border-gray-100" : "border-dashed border-amber-200"}`}
    >
      <div
        className={`h-1 w-full ${task.isPublished ? "bg-purple-400" : "bg-amber-300"}`}
      />

      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md">
              {task.quarter} · Task {task.taskNumber}
            </span>
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full
              ${task.isPublished ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
            >
              {task.isPublished ? "Published" : "Draft"}
            </span>
          </div>
          {days !== null && task.isPublished && (
            <span
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0
              ${overdue ? "bg-red-100 text-red-600" : days <= 7 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500"}`}
            >
              {overdue ? `${Math.abs(days)}d overdue` : `${days}d left`}
            </span>
          )}
        </div>

        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">
          {task.title}
        </h3>

        {task.description && (
          <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">
            {task.description}
          </p>
        )}

        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          <span className="flex items-center gap-1">
            <FileText size={10} /> {task.minWords} min words
          </span>
          {task.dueDate && (
            <span className="flex items-center gap-1">
              <Clock size={10} /> Due {fmtDate(task.dueDate)}
            </span>
          )}
        </div>

        {task.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {task.tags.slice(0, 4).map((t) => (
              <span
                key={t}
                className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium"
              >
                {t}
              </span>
            ))}
          </div>
        )}

        {canEdit && (
          <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-50">
            <button
              onClick={() => onTogglePublish(task)}
              title={task.isPublished ? "Unpublish" : "Publish"}
              className={`p-1.5 rounded-lg transition-colors
                ${task.isPublished ? "text-green-600 hover:bg-green-50" : "text-amber-600 hover:bg-amber-50"}`}
            >
              {task.isPublished ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button
              onClick={() => onEdit(task)}
              title="Edit"
              className="p-1.5 rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
            >
              <Pencil size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TASK FORM
───────────────────────────────────────────────────────────────── */

const TaskForm: React.FC<{
  initial: TaskFormProps;
  isEditing: boolean;
  isSaving: boolean;
  apiError: string | null;
  onSave: (f: TaskFormProps) => void;
  onCancel: () => void;
}> = ({ initial, isEditing, isSaving, apiError, onSave, onCancel }) => {
  const [form, setForm] = useState<TaskFormProps>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setForm(initial);
    setErrors({});
  }, [initial]);

  const set = useCallback(
    <K extends keyof TaskFormProps>(k: K, v: TaskFormProps[K]) =>
      setForm((p) => ({ ...p, [k]: v })),
    [],
  );

  const addStep = () =>
    set("instructions", [
      ...form.instructions,
      {
        _id: Math.random().toString(36),
        stepNumber: String(form.instructions.length + 1),
        instruction: "",
      },
    ]);

  const updateStep = (
    id: string,
    field: "stepNumber" | "instruction",
    val: string,
  ) =>
    set(
      "instructions",
      form.instructions.map((s) => (s._id === id ? { ...s, [field]: val } : s)),
    );

  const removeStep = (id: string) =>
    set(
      "instructions",
      form.instructions.filter((s) => s._id !== id),
    );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.quarter) e.quarter = "Quarter is required.";
    if (!form.taskNumber || isNaN(Number(form.taskNumber)))
      e.taskNumber = "Valid task number is required.";
    if (!form.title.trim()) e.title = "Title is required.";
    if (form.minWords && isNaN(Number(form.minWords)))
      e.minWords = "Must be a number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FL required>Quarter</FL>
          <Sel
            value={form.quarter}
            onChange={(e) => set("quarter", e.target.value as QuarterKey)}
            disabled={isEditing}
          >
            {QUARTERS.map((q) => (
              <option key={q} value={q}>
                {q}
              </option>
            ))}
          </Sel>
          {errors.quarter && (
            <p className="text-[11px] text-red-500 mt-1">{errors.quarter}</p>
          )}
        </div>
        <div>
          <FL required>Task number</FL>
          <Input
            type="number"
            min={1}
            placeholder="1"
            value={form.taskNumber}
            onChange={(e) => set("taskNumber", e.target.value)}
            disabled={isEditing}
          />
          {errors.taskNumber && (
            <p className="text-[11px] text-red-500 mt-1">{errors.taskNumber}</p>
          )}
        </div>
      </div>

      <div>
        <FL required>Title</FL>
        <Input
          placeholder="e.g. Governance Audit"
          value={form.title}
          onChange={(e) => set("title", e.target.value)}
          maxLength={200}
        />
        {errors.title && (
          <p className="text-[11px] text-red-500 mt-1">{errors.title}</p>
        )}
      </div>

      <div>
        <FL>Description / brief</FL>
        <Textarea
          rows={3}
          placeholder="What is this task about and why it matters…"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          maxLength={1000}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FL>Minimum words</FL>
          <Input
            type="number"
            min={50}
            placeholder="300"
            value={form.minWords}
            onChange={(e) => set("minWords", e.target.value)}
          />
          {errors.minWords && (
            <p className="text-[11px] text-red-500 mt-1">{errors.minWords}</p>
          )}
        </div>
        <div>
          <FL>Tags</FL>
          <Input
            placeholder="Governance, Ethics  (comma-sep)"
            value={form.tags}
            onChange={(e) => set("tags", e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FL>Available from</FL>
          <Input
            type="date"
            value={form.availableFrom}
            onChange={(e) => set("availableFrom", e.target.value)}
          />
        </div>
        <div>
          <FL>Due date</FL>
          <Input
            type="date"
            value={form.dueDate}
            onChange={(e) => set("dueDate", e.target.value)}
          />
        </div>
      </div>

      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
            Step-by-step instructions
          </p>
          <button
            type="button"
            onClick={addStep}
            className="text-[11px] font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            <Plus size={11} /> Add step
          </button>
        </div>
        {form.instructions.length === 0 && (
          <p className="text-[11px] text-gray-400">
            No steps yet. Add numbered instructions to guide mentees through
            this task.
          </p>
        )}
        <div className="space-y-2">
          {form.instructions.map((step, idx) => (
            <div key={step._id} className="flex items-start gap-2">
              <Input
                type="number"
                min={1}
                value={step.stepNumber}
                onChange={(e) =>
                  updateStep(step._id, "stepNumber", e.target.value)
                }
                className="w-14 shrink-0 text-center"
              />
              <Textarea
                rows={2}
                placeholder={`Step ${idx + 1} instruction…`}
                value={step.instruction}
                onChange={(e) =>
                  updateStep(step._id, "instruction", e.target.value)
                }
              />
              <button
                type="button"
                onClick={() => removeStep(step._id)}
                className="mt-2 shrink-0 p-1 text-gray-400 hover:text-red-500"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 mb-3">
          Visibility
        </p>
        <Toggle
          checked={form.isPublished}
          onChange={(v) => set("isPublished", v)}
          label="Published"
          sub="Mentees can see and start this task. Unpublished tasks are saved as drafts."
        />
      </div>

      {apiError && (
        <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
          <AlertCircle size={13} className="shrink-0" />
          {apiError}
        </div>
      )}

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 transition-all shadow-sm disabled:opacity-50"
        >
          {isSaving ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Check size={14} /> {isEditing ? "Save changes" : "Create task"}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

/* ─────────────────────────────────────────────────────────────────
   GRADE PANEL
───────────────────────────────────────────────────────────────── */

const GradePanel: React.FC<{
  sub: PendingSubmission;
  isGrading: boolean;
  apiError: string | null;
  onGrade: (comment: string, score: number) => void;
  onClose: () => void;
}> = ({ sub, isGrading, apiError, onGrade, onClose }) => {
  const [comment, setComment] = useState("");
  const [score, setScore] = useState<number>(7);
  const [err, setErr] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) {
      setErr("Feedback comment is required.");
      return;
    }
    if (score < 0 || score > 10) {
      setErr("Score must be 0–10.");
      return;
    }
    setErr("");
    onGrade(comment.trim(), score);
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              Review and grade submission
            </h2>
            <p className="text-xs text-gray-400 truncate max-w-md">
              {sub.mentee.firstName} {sub.mentee.lastName} · {sub.task.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 shrink-0">
                {sub.mentee.firstName[0]}
                {sub.mentee.lastName[0]}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {sub.mentee.firstName} {sub.mentee.lastName}
                </p>
                <p className="text-[11px] text-gray-400">
                  {sub.mentee.email} · {sub.mentee.region || "—"}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 pt-1">
              <span className="font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
                {sub.task.quarter} · Task {sub.task.taskNumber}
              </span>
              {sub.wordCount ? <span>{sub.wordCount} words</span> : null}
              {sub.submittedAt ? (
                <span>Submitted {fmtDate(sub.submittedAt)}</span>
              ) : null}
              {sub.isLate ? (
                <span className="text-red-500 font-semibold">LATE</span>
              ) : null}
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
                Task brief
              </p>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {sub.task.title}
                </h3>
                {sub.task.description && (
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                    {sub.task.description}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
                {sub.task.minWords ? (
                  <span className="inline-flex items-center gap-1">
                    <FileText size={11} />
                    {sub.task.minWords} min words
                  </span>
                ) : null}
                {sub.task.dueDate ? (
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays size={11} />
                    Due {fmtDate(sub.task.dueDate)}
                  </span>
                ) : null}
              </div>

              {sub.task.instructions && sub.task.instructions.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    Instructions
                  </p>
                  <ol className="space-y-2">
                    {sub.task.instructions.map((step, idx) => (
                      <li
                        key={`${step.stepNumber}-${idx}`}
                        className="flex items-start gap-3"
                      >
                        <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                          {step.stepNumber}
                        </span>
                        <span className="text-sm text-gray-700 leading-relaxed">
                          {step.instruction}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-gray-500">
                Student submission
              </p>
              {sub.wordCount ? (
                <span className="text-[11px] text-gray-400">
                  {sub.wordCount} words
                </span>
              ) : null}
            </div>
            <div className="p-4">
              {sub.draftContent ? (
                <div className="max-h-96 overflow-y-auto rounded-lg bg-white">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {sub.draftContent}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  No submission text available.
                </p>
              )}

              {sub.attachmentName && sub.attachmentUrl && (
                <div className="mt-4">
                  <a
                    href={sub.attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700"
                  >
                    <Paperclip size={14} />
                    {sub.attachmentName}
                  </a>
                </div>
              )}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4 border-t border-gray-100 pt-5"
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-700">
                  Score (0–10)
                </p>
                <ScoreRing score={score} size={48} />
              </div>
              <input
                type="range"
                min={0}
                max={10}
                step={0.5}
                value={score}
                onChange={(e) => setScore(Number(e.target.value))}
                className="w-full accent-purple-600"
              />
              <div className="flex justify-between text-[11px] text-gray-400 mt-1">
                <span>0 — Needs improvement</span>
                <span>10 — Exceptional</span>
              </div>
            </div>

            <div>
              <FL required>Feedback comment</FL>
              <Textarea
                rows={5}
                placeholder="Provide constructive feedback on this submission. Be specific about strengths and areas for improvement…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={2000}
              />
              <p className="text-right text-[11px] text-gray-400 mt-0.5">
                {comment.length}/2000
              </p>
            </div>

            {(err || apiError) && (
              <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                <AlertCircle size={12} className="shrink-0" />
                {err || apiError}
              </div>
            )}

            <button
              type="submit"
              disabled={isGrading}
              className="w-full py-3 text-sm font-semibold bg-purple-600 text-white rounded-xl hover:bg-purple-700 active:scale-95 transition-all disabled:opacity-50"
            >
              {isGrading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Submitting
                  grade…
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check size={14} /> Submit grade
                </span>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SUBMISSION ROW
───────────────────────────────────────────────────────────────── */

const SubmissionRow: React.FC<{
  sub: PendingSubmission;
  canGrade: boolean;
  onGrade: (sub: PendingSubmission) => void;
}> = ({ sub, canGrade, onGrade }) => (
  <div className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors group">
    <div className="shrink-0 w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700">
      {sub.mentee.firstName[0]}
      {sub.mentee.lastName[0]}
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {sub.mentee.firstName} {sub.mentee.lastName}
        </p>
        {sub.isLate && (
          <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full shrink-0">
            LATE
          </span>
        )}
      </div>
      <p className="text-[11px] text-gray-400 truncate">
        {sub.mentee.email} · {sub.mentee.region || "—"}
      </p>
    </div>

    <div className="shrink-0 hidden sm:block text-right">
      <span className="text-[11px] font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded">
        {sub.task.quarter} · Task {sub.task.taskNumber}
      </span>
      <p className="text-[11px] text-gray-500 mt-0.5 truncate max-w-40">
        {sub.task.title}
      </p>
    </div>

    {sub.wordCount && (
      <div className="shrink-0 hidden md:block text-right">
        <p className="text-xs font-semibold text-gray-700">{sub.wordCount}</p>
        <p className="text-[10px] text-gray-400">words</p>
      </div>
    )}

    <div className="shrink-0 hidden lg:block text-right">
      <p className="text-[11px] text-gray-500">{fmtDate(sub.submittedAt)}</p>
    </div>

    {canGrade ? (
      <button
        onClick={() => onGrade(sub)}
        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:scale-95 transition-all"
      >
        <Star size={12} /> Review & Grade
      </button>
    ) : (
      <button
        onClick={() => onGrade(sub)}
        className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-all"
      >
        <Eye size={12} /> View
      </button>
    )}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */

const AdminTasks: React.FC = () => {
  const user = useAppSelector((s) => s.auth.user)!;
  const role = (user.role ?? "portal_manager") as UserRole;
  const canEdit = CONTENT_ROLES.includes(role);
  const canGrade = GRADER_ROLES.includes(role);

  const [activeTab, setActiveTab] = useState<"definitions" | "grading">(
    canEdit ? "definitions" : "grading",
  );

  const [qFilter, setQFilter] = useState<QuarterKey | "all">("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft"
  >("all");
  const [search, setSearch] = useState("");
  const [gradingQ, setGradingQ] = useState<QuarterKey | "all">("all");

  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<TaskWithSubmission | null>(null);
  const [gradeTarget, setGradeTarget] = useState<PendingSubmission | null>(
    null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [gradeError, setGradeError] = useState<string | null>(null);

  const { data: tasksRes, isLoading: tasksLoading } = useGetTasksQuery({
    ...(qFilter !== "all" ? { quarter: qFilter } : {}),
    ...(canEdit ? { includeAll: true } : {}),
  });

  const { data: pendingRes, isLoading: pendingLoading } =
    useGetPendingSubmissionsQuery(
      gradingQ !== "all" ? { quarter: gradingQ } : {},
      { skip: activeTab !== "grading" },
    );

  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const [gradeTask, { isLoading: isGrading }] = useGradeSubmissionMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTasks = tasksRes?.data ?? [];
  const pending = pendingRes?.data ?? [];

  const filteredTasks = useMemo(() => {
    let items = allTasks;
    if (statusFilter === "published")
      items = items.filter((t) => t.isPublished);
    if (statusFilter === "draft") items = items.filter((t) => !t.isPublished);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.tags?.some((tag) => tag.toLowerCase().includes(q)),
      );
    }
    return items;
  }, [allTasks, statusFilter, search]);

  const draftCount = allTasks.filter((t) => !t.isPublished).length;
  const publishedCount = allTasks.filter((t) => t.isPublished).length;

  const handleSave = async (form: TaskFormProps) => {
    setFormError(null);
    try {
      const payload = {
        quarter: form.quarter,
        taskNumber: Number(form.taskNumber),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        minWords: form.minWords ? Number(form.minWords) : 300,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        availableFrom: form.availableFrom || undefined,
        dueDate: form.dueDate || undefined,
        isPublished: form.isPublished,
        instructions: form.instructions
          .filter((s) => s.instruction.trim())
          .map(({ stepNumber, instruction }) => ({
            stepNumber: Number(stepNumber),
            instruction: instruction.trim(),
          })),
      };

      if (editTarget) {
        await updateTask({ id: editTarget.id, ...payload }).unwrap();
      } else {
        await createTask(payload).unwrap();
      }
      setShowForm(false);
      setEditTarget(null);
    } catch (err: any) {
      setFormError(
        err?.data?.error ?? "Something went wrong. Please try again.",
      );
    }
  };

  const handleTogglePublish = async (task: TaskWithSubmission) => {
    try {
      await updateTask({
        id: task.id,
        isPublished: !task.isPublished,
      }).unwrap();
    } catch {
      // ignore
    }
  };

  const handleGrade = async (comment: string, score: number) => {
    if (!gradeTarget || !canGrade) return;
    setGradeError(null);
    try {
      await gradeTask({
        submissionId: gradeTarget.id,
        comment,
        score,
      }).unwrap();
      setGradeTarget(null);
    } catch (err: any) {
      setGradeError(
        err?.data?.error ?? "Could not submit grade. Please try again.",
      );
    }
  };

  const initialForm = useMemo<TaskFormProps>(
    () => (editTarget ? taskToForm(editTarget) : EMPTY_FORM),
    [editTarget],
  );

  return (
    <div className="space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600 mb-1">
            Programme Management
          </p>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="mt-1 text-sm text-gray-500">
            {canEdit
              ? "Create, publish, and manage task definitions. Review submissions and grade mentees."
              : "Review submissions and grade mentees within your coverage area."}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditTarget(null);
              setFormError(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-xl hover:bg-purple-700 active:scale-95 transition-all shadow-sm shrink-0"
          >
            <Plus size={16} /> Add task
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "Total tasks",
            value: allTasks.length,
            colour: "text-purple-700",
            icon: <ClipboardList size={12} className="text-purple-400" />,
          },
          {
            label: "Published",
            value: publishedCount,
            colour: "text-green-700",
            icon: <Eye size={12} className="text-green-400" />,
          },
          {
            label: "Drafts",
            value: draftCount,
            colour: draftCount > 0 ? "text-amber-600" : "text-gray-400",
            icon: <EyeOff size={12} className="text-amber-400" />,
          },
          {
            label: "Pending grades",
            value: pending.length,
            colour: pending.length > 0 ? "text-red-600" : "text-gray-400",
            icon: <Star size={12} className="text-red-400" />,
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4"
          >
            <div className="flex items-center gap-1.5 mb-1 text-[11px] text-gray-400">
              {s.icon}
              {s.label}
            </div>
            {tasksLoading ? (
              <div className="h-7 w-10 bg-gray-100 animate-pulse rounded-lg mt-1" />
            ) : (
              <p className={`text-2xl font-bold ${s.colour}`}>{s.value}</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          {
            key: "definitions",
            label: "Task Definitions",
            icon: <ClipboardList size={13} />,
          },
          {
            key: "grading",
            label: `Grading Queue${pending.length > 0 ? ` (${pending.length})` : ""}`,
            icon: <Star size={13} />,
          },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as "definitions" | "grading")}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg transition-all
              ${activeTab === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "definitions" && (
        <>
          {!canEdit && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
              <Info size={13} className="shrink-0" />
              You have read-only access to task definitions.
            </div>
          )}

          {role === "regional_rep" && canEdit && (
            <div className="flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-xs text-purple-700">
              <Info size={13} className="shrink-0" />
              As a Regional Representative, you can create and manage task
              definitions and grade submissions from your coverage area.
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search tasks…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-purple-300 placeholder-gray-400 transition"
              />
            </div>
            <Sel
              value={qFilter}
              onChange={(e) => setQFilter(e.target.value as QuarterKey | "all")}
              className="sm:w-36"
            >
              <option value="all">All quarters</option>
              {QUARTERS.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </Sel>
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 shrink-0">
              {(["all", "published", "draft"] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all
                    ${statusFilter === s ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {tasksLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Bone key={i} className="h-52" />
              ))}
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center">
                <ClipboardList size={22} className="text-gray-300" />
              </div>
              <p className="text-sm font-semibold text-gray-600">
                {search ? "No tasks match this search" : "No tasks yet"}
              </p>
              <p className="text-xs text-gray-400 max-w-xs">
                {canEdit
                  ? `Click "Add task" to create the first task definition.`
                  : "No tasks have been published yet."}
              </p>
              {canEdit && !search && (
                <button
                  onClick={() => {
                    setEditTarget(null);
                    setFormError(null);
                    setShowForm(true);
                  }}
                  className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-purple-600 hover:text-purple-700"
                >
                  <Plus size={14} /> Add first task
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-[11px] text-gray-400">
                {filteredTasks.length} task
                {filteredTasks.length !== 1 ? "s" : ""}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    canEdit={canEdit}
                    onEdit={(t) => {
                      setEditTarget(t);
                      setFormError(null);
                      setShowForm(true);
                    }}
                    onTogglePublish={handleTogglePublish}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {activeTab === "grading" && (
        <>
          {!canGrade && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
              <Info size={13} className="shrink-0" />
              You have read-only access to the grading queue.
            </div>
          )}

          <div className="flex items-center gap-3">
            <p className="text-xs font-semibold text-gray-600">Filter:</p>
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setGradingQ("all")}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${gradingQ === "all" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
              >
                All
              </button>
              {QUARTERS.map((q) => (
                <button
                  key={q}
                  onClick={() => setGradingQ(q)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all ${gradingQ === q ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
              <Star size={14} className="text-purple-400" />
              <p className="text-xs font-bold text-gray-700">
                Pending submissions
                {!pendingLoading && (
                  <span className="ml-2 text-gray-400 font-normal">
                    ({pending.length})
                  </span>
                )}
              </p>
            </div>

            {pendingLoading ? (
              <div className="p-4 space-y-3">
                {[0, 1, 2, 3].map((i) => (
                  <Bone key={i} className="h-14" />
                ))}
              </div>
            ) : pending.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
                <CheckCircle2 size={28} className="text-green-300" />
                <p className="text-sm font-semibold text-gray-600">
                  All submissions graded
                </p>
                <p className="text-xs text-gray-400">
                  No pending submissions for the selected quarter.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {pending.map((sub) => (
                  <SubmissionRow
                    key={sub.id}
                    sub={sub}
                    canGrade={canGrade}
                    onGrade={setGradeTarget}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {showForm && canEdit && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div
            className="flex-1 bg-black/40"
            onClick={() => {
              setShowForm(false);
              setEditTarget(null);
            }}
          />
          <div className="w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden">
            <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-base font-bold text-gray-900">
                  {editTarget
                    ? `Edit task — ${editTarget.quarter} · Task ${editTarget.taskNumber}`
                    : "Create task definition"}
                </h2>
                <p className="text-xs text-gray-400">
                  {editTarget
                    ? editTarget.title
                    : "Fill in the required fields."}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditTarget(null);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <TaskForm
                initial={initialForm}
                isEditing={!!editTarget}
                isSaving={isCreating || isUpdating}
                apiError={formError}
                onSave={handleSave}
                onCancel={() => {
                  setShowForm(false);
                  setEditTarget(null);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {gradeTarget && (
        <GradePanel
          sub={gradeTarget}
          isGrading={isGrading}
          apiError={gradeError}
          onGrade={handleGrade}
          onClose={() => {
            setGradeTarget(null);
            setGradeError(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminTasks;
